import { api } from '@/lib/api/client';
import type { JournalResponse } from '@/lib/api/types';
import type { InternationalArrival, InternationalArrivalCriteria } from '@/types/internationalArrival';

/**
 * The international arrivals journal.
 *
 * **ADMIN and AAK only, the read included.** Narrower than the online table: USER
 * and KGA_USER may read the schedule and are answered 403 here, so the route is
 * guarded rather than the screen merely hiding its buttons.
 *
 * `POST /international-arrival/save` is deliberately NOT wrapped. It creates a
 * journal row by hand, and the endpoint's own documentation calls it rarely the
 * right thing: the index is built automatically for every international arrival the
 * schedule knows about, so a row somebody has to create means the FLIGHT is missing
 * from the schedule — and the fix for that is upstream, in whatever should have
 * reported it. A row saved with neither a schedule link nor a triple matching a real
 * flight is inert: nothing will ever fill it in. Offering that on this screen would
 * be offering a way to make an orphan.
 */

/** A page of the journal. A POST, because the filter travels as a body. */
export const fetchInternationalArrivals = (
  criteria: InternationalArrivalCriteria,
  signal?: AbortSignal,
): Promise<JournalResponse<InternationalArrival>> =>
  api.post<JournalResponse<InternationalArrival>>('/international-arrival/filter', prune(criteria), signal);

/**
 * Drops what should not be sent.
 *
 * A blank string is not a filter — the server trims and ignores it — and `rows: 0`
 * in particular is removed rather than sent: it returns no rows beside a real total
 * count, which looks exactly like a server fault. `isActive: false` is a REAL filter
 * (show me the withdrawn ones) so booleans are passed through untouched; only
 * `undefined` means "no opinion".
 */
const prune = (criteria: InternationalArrivalCriteria): InternationalArrivalCriteria => {
  const body: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(criteria)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if ((key === 'rows' || key === 'pageNum') && Number(value) < 1) continue;

    body[key] = typeof value === 'string' ? value.trim() : value;
  }

  return body as InternationalArrivalCriteria;
};

/**
 * There is no write here, and that is deliberate.
 *
 * `POST /international-arrival/update` is what writes the ground-handling times —
 * and `firstPax`/`lastPax` in particular, which NO integration reports. This screen
 * is a read-only journal, so nothing in this app calls it: those two columns are
 * filled in elsewhere or not at all.
 *
 * `POST /international-arrival/save` is absent for its own reasons. It creates a
 * journal row by hand, and the endpoint's documentation calls that rarely right: the
 * index is built automatically for every international arrival the schedule knows
 * about, so a row somebody has to create means the FLIGHT is missing from the
 * schedule and the fix is upstream. A row saved with neither a schedule link nor a
 * triple matching a real flight is inert — nothing will ever fill it in.
 */
